import React, { useState } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import TextField from "@mui/material/TextField";
import { getCostCenterValue } from "../capture/FieldsIdentifiers";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function CostCenterFilterModal({ value, costCenters = [], onSave, onClose }) {
  // Convert single value to array for backward compatibility
  const initialSelectedIds = Array.isArray(value) ? value : (value ? [value] : []);
  const [selectedCostCenterIds, setSelectedCostCenterIds] = useState(initialSelectedIds);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredCostCenters = searchTerm
    ? costCenters.filter(cc => 
        getCostCenterValue(cc)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cc.costCenterCode?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : costCenters;

  const handleToggle = (costCenterId) => {
    setSelectedCostCenterIds(prev => 
      prev.includes(costCenterId)
        ? prev.filter(id => id !== costCenterId)
        : [...prev, costCenterId]
    );
  };

  const handleSelectAll = () => {
    const allIds = filteredCostCenters.map(cc => cc.id);
    const allSelected = allIds.every(id => selectedCostCenterIds.includes(id));
    
    if (allSelected) {
      // Deselect all filtered cost centers
      setSelectedCostCenterIds(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      // Select all filtered cost centers
      setSelectedCostCenterIds(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleApply = () => {
    onSave(selectedCostCenterIds);
    onClose();
  };

  const handleCancel = () => {
    setSelectedCostCenterIds(initialSelectedIds);
    onClose();
  };

  const allFilteredIds = filteredCostCenters.map(cc => cc.id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedCostCenterIds.includes(id));
  const indeterminate = allFilteredIds.some(id => selectedCostCenterIds.includes(id)) && !allSelected;

  return (
    <Dialog open onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          Cost Center Filters
        </Typography>
        <IconButton
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
          />
        </Box>

        <Box sx={{ maxHeight: 300, overflow: "auto" }}>
          {filteredCostCenters.map((costCenter) => {
            const isChecked = selectedCostCenterIds.includes(costCenter.id);
            return (
              <Box
                key={costCenter.id}
                onClick={() => handleToggle(costCenter.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  p: 1,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "action.hover"
                  }
                }}
              >
                <Checkbox
                  checked={isChecked}
                  icon={icon}
                  checkedIcon={checkedIcon}
                  sx={{ mr: 1 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {getCostCenterValue(costCenter)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                    {costCenter.costCenterCode}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        
        {selectedCostCenterIds.length > 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              {selectedCostCenterIds.length} cost center{selectedCostCenterIds.length !== 1 ? 's' : ''} selected
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleApply}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
